/**
 * Los colores de la marca, llevados a un informe que se LEA bien (29-09-2026).
 *
 * El texto sigue en tinta neutra; la marca va en lo que adorna (raya, barra del
 * día, píldoras, primera tarjeta) y en la banda. Un color de marca claro (un
 * amarillo) no se descarta: se OSCURECE lo justo hasta que se lee — 3:1 para lo
 * que es gráfico (WCAG 1.4.11) y 4.5:1 para lo que es texto (WCAG 1.4.3).
 * Los colores con significado (biológico/químico, la plaga) no cambian.
 */
import { PDF_V2, type RGB } from "@/lib/pdf/brand";
import type { MarcaInformes } from "./tipos";

export type ColoresPdf = typeof PDF_V2.color;

export const BLANCO: RGB = [255, 255, 255];
export const NEGRO: RGB = [0, 0, 0];

/** «#RRGGBB» (o sin almohadilla) → RGB; cualquier otra cosa, null. */
export function hexARgb(hex: string | null | undefined): RGB | null {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex ?? "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbAHex(c: RGB): string {
  return `#${c.map((v) => Math.round(v).toString(16).padStart(2, "0")).join("").toUpperCase()}`;
}

const canal = (v: number) => {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

/** Luminancia relativa (WCAG 2.x). */
export function luminancia(c: RGB): number {
  return 0.2126 * canal(c[0]) + 0.7152 * canal(c[1]) + 0.0722 * canal(c[2]);
}

/** Contraste WCAG entre dos colores (1–21). */
export function contraste(a: RGB, b: RGB): number {
  const la = luminancia(a);
  const lb = luminancia(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** `t` = 0 → a; `t` = 1 → b. */
export function mezcla(a: RGB, b: RGB, t: number): RGB {
  return [0, 1, 2].map((i) => Math.round(a[i] * (1 - t) + b[i] * t)) as RGB;
}

/**
 * El mismo color, oscurecido LO JUSTO para llegar a `ratio` sobre `fondo`
 * (búsqueda binaria hacia el negro). Si ya llega, se devuelve tal cual.
 */
export function oscurecerHasta(c: RGB, ratio: number, fondo: RGB = BLANCO): RGB {
  if (contraste(c, fondo) >= ratio) return c;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (contraste(mezcla(c, NEGRO, mid), fondo) >= ratio) hi = mid;
    else lo = mid;
  }
  return mezcla(c, NEGRO, hi);
}

/** El texto que va ENCIMA de un color: blanco si se lee (4.5:1), si no, la tinta. */
export function textoSobre(fondo: RGB, tinta: RGB = PDF_V2.color.ink): RGB {
  return contraste(BLANCO, fondo) >= 4.5 ? BLANCO : tinta;
}

/**
 * ¿Se puede pintar una BANDA de este color? No si es negro o gris muy oscuro, ni
 * casi blanco: una banda negra no gustó (Pablo, 29-09-2026, muestra F0: «me gustan
 * todos menos el negro») y una casi blanca no se ve. Con esos colores de principal
 * la cabecera va en blanco.
 */
export function admiteBanda(hex: string | null | undefined): boolean {
  const c = hexARgb(hex);
  if (!c) return false;
  const max = Math.max(...c);
  const min = Math.min(...c);
  const croma = (max - min) / 255;
  const luz = (max + min) / 2 / 255;
  if (croma < 0.12 && luz < 0.3) return false; // negro, gris oscuro
  if (croma < 0.08 && luz > 0.92) return false; // casi blanco
  return true;
}

/**
 * El texto SECUNDARIO encima de un color (las líneas y el periodo sobre la banda):
 * el texto principal mezclado con el fondo, pero solo si sigue leyéndose a 4.5:1;
 * si no, el mismo texto principal.
 */
export function textoSuaveSobre(fondo: RGB, texto: RGB): RGB {
  for (const t of [0.25, 0.15]) {
    const c = mezcla(texto, fondo, t);
    if (contraste(c, fondo) >= 4.5) return c;
  }
  return texto;
}

/** Los colores de la marca ya ajustados, para quien pinta la cabecera y el pie. */
export interface ColoresMarca {
  /** Banda (estilo «banda»), tal cual la eligió la empresa. */
  principal: RGB;
  /** El principal cuando es TEXTO sobre blanco (nombre de la empresa). */
  principalTexto: RGB;
  /** Raya, barra del día, primera tarjeta: gráfico, 3:1. */
  acento: RGB;
  /** El acento cuando es TEXTO (la píldora del día): 4.5:1. */
  acentoTexto: RGB;
  /** Fondo suave de la píldora del día. */
  acentoSuave: RGB;
  /** Texto encima de la banda. */
  sobreBanda: RGB;
}

export function coloresMarca(marca: MarcaInformes): ColoresMarca {
  const base = PDF_V2.color;
  const principal = hexARgb(marca.colorPrincipal) ?? base.ink;
  const acentoElegido = hexARgb(marca.colorAcento) ?? principal;
  return {
    principal,
    principalTexto: oscurecerHasta(principal, 4.5),
    acento: oscurecerHasta(acentoElegido, 3),
    acentoTexto: oscurecerHasta(acentoElegido, 4.5),
    acentoSuave: mezcla(acentoElegido, BLANCO, 0.88),
    sobreBanda: textoSobre(principal),
  };
}

/**
 * La paleta completa del informe. Sin marca es la de siempre, el MISMO objeto:
 * un informe sin marca sale idéntico al de hoy.
 */
export function coloresPdf(marca: MarcaInformes | null): ColoresPdf {
  const base = PDF_V2.color;
  if (!marca) return base;
  const m = coloresMarca(marca);
  const neutroSuave: RGB = [246, 247, 249]; // #F6F7F9: banda de cada aplicación, sin tinte
  return {
    ...base,
    teal: m.acento,
    tealD: m.acentoTexto,
    tealSoft: m.acentoSuave,
    appTint: neutroSuave,
    // «ha · €/ha · subtotal» dejan el verde de Ulture: tinta de la casa.
    green: base.secondary,
    greenD: base.ink,
    kpiTick: [m.acento, m.principalTexto, base.kpiTick[2], base.kpiTick[3]],
    grad: { from: m.principal, mid: m.principal, to: m.principal },
  };
}
